import PROOF_EVENTS from "./proofEvents";
import { EXPIRED_COOKIE_DATE, TYPES_ENUM, DOC_TYPES } from "./constants";
import EnvService from "../env.settings"
import { ONBOARDING_TODO_ITEMS } from "./constants";

const { AnnotationCommentThreadUpdated, ApproverAutoReminderQueued } = PROOF_EVENTS
const { STRING } = TYPES_ENUM;
const { VIDEO, AUDIO, IMAGE, DOCUMENT } = DOC_TYPES;

export const getAppEventProperty = (event, field, defaultValue) => {
    return _.get(event, `appEventObject.${field}`, defaultValue);
}

export const hasCommentAnnotation = (event) => {
    return !!getAppEventProperty(event, 'commentAnnotation') || !!getAppEventProperty(event, 'additionalData.commentAnnotation');
}

export const sortEventsChronologically = (events) => {
    return events.sort((a, b) => {
      const getCreatedAt = (event) => {
        let eventCreatedAt = event.createdAt;
        if (event.eventType === AnnotationCommentThreadUpdated && hasCommentAnnotation(event)) 
          eventCreatedAt = getAppEventProperty(event, "proofAnnotation.createdAt") || getAppEventProperty(event, "additionalData.commentAnnotation.createdAt");

        const localDateTime = new Date(eventCreatedAt);
        const isUTC = typeof eventCreatedAt === STRING && eventCreatedAt.endsWith('Z');

        const timezoneOffset = localDateTime.getTimezoneOffset() * 60000;
        const adjustedDateTime = isUTC ? localDateTime : new Date(localDateTime.getTime() - timezoneOffset);

        return adjustedDateTime;
      };

      return getCreatedAt(b) - getCreatedAt(a);
    });
}

const { webbApp } = EnvService.instance;
export const isCustomDomain = window.location.hostname !== new URL(webbApp).hostname && window.location.hostname !== "localhost";

export const updateFaviconAndTitle = ({ accountLogo = "", accountTitle = "" }) => {
  try {
    if (accountTitle) document.title = `${accountTitle} App`;

    if (accountLogo) {
      localStorage.setItem(
        `accountLogo${window.location.hostname}`,
        accountLogo
      );
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "shortcut icon";
        link.type = "image/x-icon";
        document.head.appendChild(link);
      }
      link.href = accountLogo;
    }
  } catch (error) {
    console.error("Error updating Meta Details:", error);
  }
};

export const getAccountLogo = () => {
  return localStorage.getItem(`accountLogo${window.location.hostname}`) || 'img/logo-large.svg';
}

export const isEligibleForFirstTimeOffer = (auth, isFreeUser = true) => {
  if(!isFreeUser) return false;
  const onboardedAt = auth?.onboardedAt;
  if (!onboardedAt || auth?.onboardingStep === 1) return false;

  const twoHoursAfterOnboarding = new Date(onboardedAt + 'Z');
  twoHoursAfterOnboarding.setHours(twoHoursAfterOnboarding.getHours() + 2);

  return new Date() <= twoHoursAfterOnboarding;
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

export const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie =
    name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=${EXPIRED_COOKIE_DATE}; path=/; SameSite=Lax`;
};

export const getDefaultThumbnail = (fileType) => {
  switch (fileType) {
    case VIDEO:
      return "/img/video-thumbnail.png";
    case AUDIO:
      return "/img/audio-thumbnail.svg";
    case IMAGE:
      return "/img/image-thumbnail.png";
    case DOCUMENT:
      return "/img/pdf-thumbnail.png";
    default:
      return "";
  }
};

export function deduplicateApproverReminders(events) {
  const seenEmails = new Set();
  return events.filter((s) => {
    if (s.eventType === ApproverAutoReminderQueued) {
      const emailMatch = s.description.match(/<([^>]+)>/);
      if (!emailMatch) {
        return !seenEmails.has(s.id);
      }
      
      const email = emailMatch[1];
      if (seenEmails.has(email)) {
        return false;
      } else {
        seenEmails.add(email);
        return true;
      }
    }
    return true;
  });
}

export function sortApproverRemindersFirst(events) {
  return events.sort((a, b) => {
    const isAQueued = a.eventType === ApproverAutoReminderQueued;
    const isBQueued = b.eventType === ApproverAutoReminderQueued;
    if (isAQueued && !isBQueued) return -1;
    if (!isAQueued && isBQueued) return 1;
    if (isAQueued && isBQueued) {
      return new Date(b.queuedActionAt) - new Date(a.queuedActionAt);
    }
    return 0;
  })
}

export const getPendingTodoItems = (onboardingState) => {
  if (!onboardingState?.todoState || !onboardingState?.modelState) return [];

  const hasUnseenModals = Object.values(onboardingState.modelState).includes(false);
  if (!hasUnseenModals) return [];

  const todoMappings = {
    hasCreatedProof: ONBOARDING_TODO_ITEMS.CREATE_PROOF,
    hasReviewLinkRegistered: ONBOARDING_TODO_ITEMS.SHARE_REVIEW_LINK,
    hasEmailTemplateCreated: ONBOARDING_TODO_ITEMS.CREATE_EMAIL_TEMPLATE,
    hasUsedWorkflow: ONBOARDING_TODO_ITEMS.SEND_WORKFLOW,
  };

  const todoState = onboardingState.todoState;

  return Object.entries(todoMappings)
    .filter(([key]) => !todoState[key])
    .map(([, item]) => item);
};

export const getCurrentDateString = () => {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
};

export const getTimeBasedGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
};
